import { useDispatch, useSelector } from "react-redux";
import {
  selectActiveWinery,
  selectAnalysis,
  selectStoredAnalysisResults,
  selectConfigs,
  selectDrugs,
  selectLots,
  selectOperations,
  selectTanks,
  selectTreatments,
  selectUsers,
  selectUserSockets,
  selectExams,
  selectOperationById,
  selectOperationByIdMemo,
  selectCompletedOperations,
  selectWineMaking,
  selectUnits,
  selectWarehouseByType,
} from "../../context/selectors";

// NEW: Import CacheManager for surgical cache updates
import CacheManager from "../../services/cacheManager";
import { buildOperationVersions, conflictsToAffectedEntities } from "../../services/syncManager";
import { NavLink, useLocation, useNavigate, useParams } from "react-router-dom";
import useAxiosPrivate from "../../hooks/useAxiosPrivate";
import SecondaryBar from "../SecondaryBar";
import AuditLogWidget from "../AuditLog";
import useLabels from "../../hooks/useLabels";
import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { socket } from "../../socket";
import {
  Button,
  ConfirmationModal,
  CTA,
  Modal,
  OperationRecap,
  Tooltip,
} from "../Generic";
import {
  addAnalysisSample,
  addOperationNote,
  completeOperation,
  deleteOperation,
  getAnalysisResults,
  getDrugs,
  getExams,
  getLots,
  getOperationById,
  getOperations,
  getOperationsByMainId,
  getPDF,
  getProtocols,
  getProtocolApplicationStatus,
  getRepetitiveOperationsById,
  getRollbackImpact,
  getStepVirtualState,
  getTanks,
  getUnits,
  getUsers,
  getWarehouseByType,
  requestPDF,
  rollbackOperation,
  startOperation,
} from "../../services/utils";
import {
  empty,
  removeOp,
  setOperations,
  updateNote,
  updateOp,
} from "../../context/operations/operationsSlice";
import {
  addCompletedOp,
  setCompletedOperations,
  removeCompletedOp
} from "../../context/completedOperations/completedOperationsSlice";
import { removeExpOp } from "../../context/expOperations/expOperationsSlice";
import { setUsers } from "../../context/users/usersSlice";
import { emptyTanks, setTanks } from "../../context/tanks/tanksSlice";
import { setWarehouse } from "../../context/warehouse/warehouseSlice";
import {
  AnalysisDetail,
  AutomaticDetail,
  BottlingDetail,
  CutDetail,
  DestemmingDetail,
  TreatmentDetail,
} from "./Details";
import ProtocolApplicationBanner from "./ProtocolApplicationBanner";
import { ROLES } from "../../constants/base";
import useAuth from "../../hooks/useAuth";
import { setAnalysis } from "../../context/protocols/analysisSlice";
import { setDrugs } from "../../context/substances/drugsSlice";
import { emptyLots, setLots } from "../../context/lots/lotsSlice";
import { setTreatments } from "../../context/protocols/treatmentsSlice";
import {
  addStoredAnalysisResults,
} from "../../context/substances/storedAnalysisResultsSlice";
import { toast } from "react-toastify";
import { useFieldArray, useForm, useWatch } from "react-hook-form";
import * as XLSX from "xlsx";

import "./styles/index.scss";
import { format } from "date-fns";
import { setExams } from "../../context/substances/examsSlice";
import { ChatArea, Switch } from "../FormComponents";
import { Label } from "recharts";
import { setWineMaking } from "../../context/protocols/wineMakingSlice";
import { computeOperationRow, computeRequiredNumericQty, computeRequiredWarehouseQty, getOperationUrlSegment, getWarehouseUnit, groupBySubstanceId, isAnalysisExecutableOrSample, isDrugBatchInteractive, isOperationExecutable, isSrcTanksStillInvalid } from "../../services/common";
import { toastAxios } from "../../services/toastAxios";
import { getInputTankUnit, inputToLiters } from "../../services/tankUnit";
import { setUnits } from "../../context/units/unitsSlice";

const OperationsDetail = ({ isMobile }) => {
  const [getLabel] = useLabels();
  const activeWinery = useSelector(selectActiveWinery);
  const { id, type: rawType } = useParams();
  const type = rawType?.toUpperCase?.();
  const operations = useSelector(selectOperations);
  const users = useSelector(selectUsers);
  const tanks = useSelector(selectTanks);
  const lots = useSelector(selectLots);
  const units = useSelector(selectUnits);
  const analysis = useSelector(selectAnalysis);
  const drugs = useSelector(selectDrugs);
  const exams = useSelector(selectExams);
  const treatments = useSelector(selectTreatments);
  const winemakingProtocols = useSelector(selectWineMaking);
  const machineries = useSelector(selectWarehouseByType("machineries"));
  const configs = useSelector(selectConfigs);
  const userSockets = useSelector(selectUserSockets);
  const dispatch = useDispatch();
  const axiosPrivate = useAxiosPrivate();
  const navigate = useNavigate();
  const location = useLocation();
  // Completed operations are reached through the "history" route segment
  // (/programming/history/:type/:id). Rollback is only offered there.
  const isHistoryView = location?.pathname?.includes("/programming/history/");
  // const [operation, setOperation] = useState({});
  // Use selectOperationByIdMemo to properly memoize the selector and detect Redux updates
  const selectOpById = useMemo(() => selectOperationByIdMemo(Number(id)), [id]);
  const operation = useSelector(selectOpById);
  // Effective operation type: the loaded operation's real type (e.g. legacy
  // TANNISSAGE) drives rendering/labels/links, while the URL segment may be the
  // generic "add". Falls back to the URL type while the operation is loading.
  const effectiveType = operation?.type || type;
  const pageName = getLabel(`operationTitle${effectiveType}`);

  const [analysisData, setAnalysisData] = useState([]);
  // const [completedOperations2, setCompletedOperations2] = useState([]);
  const completedOperations = useSelector(selectCompletedOperations);
  const [analysisResults, setAnalysisResults] = useState([]);
  const [filteredSubstances, setFilteredSubstances] = useState([]);
  const [previousTreatments, setPreviousTreatments] = useState([]);
  const [step, setStep] = useState(0);
  const [isOpen, setIsOpen] = useState(false);
  const [isImport, setIsImport] = useState(false);
  const [isManual, setIsManual] = useState(false);
  const [isRollbackOpen, setIsRollbackOpen] = useState(false);
  const [rollbackImpact, setRollbackImpact] = useState(null);
  const [rollbackImpactLoading, setRollbackImpactLoading] = useState(false);
  const [isRollingBack, setIsRollingBack] = useState(false);
  const [file, setFile] = useState(null);
  const [key, setKey] = useState(0);
  const [analysisState, setAnalysisState] = useState("NOT_SAMPLED"); // [ NOT_SAMPLED, NOT_INSERTED, INSERTING ]
  const { auth } = useAuth();
  const role = auth?.role || ROLES.GUEST;
  const storedAnalysisResults = useSelector(selectStoredAnalysisResults) || [];
  // true when current user in a USER and is in the cellarmans list for the given operation
  const isCellarmanEnabled =
    operation?.cellarman_ids?.includes(auth?.user_id) && role === 3;

  
  const {
    reset,
    register,
    formState: { errors, isValid },
    getValues,
    setValue,
    setFocus,
    setError,
    clearErrors,
    control,
  } = useForm({
    defaultValues: {
      actual: {},
      drug_batches: {},
      temperature: {},
    },
    mode: "onTouched",
    reValidateMode: "onTouched",
  });

  const completed = useWatch({ control, name: 'completed' }) || {};
  const drops = useWatch({ control, name: 'actual' }) || {};
  useWatch({ control, name: 'column.tanks' }); // keeps renderButtons reactive when analysis values are typed
  const totalActualDest = inputToLiters(
    Object.values(drops).reduce((s, v) => s + Number(v || 0), 0),
    getInputTankUnit()
  );

  const [totalTreatmentVolume, setTotalTreatmentVolume] = useState(0);
  const [isBatchSelectorModal, setIsBatchSelectorModal] = useState(type === "STABILIZATION_DYNAMIC" ? "BATCH_SELECTOR" : "");
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [isModifyMode, setIsModifyMode] = useState(-1);
  const [isEnoughStock, setIsEnoughStock] = useState(type === "STABILIZATION_DYNAMIC");

  // Virtual state for protocol operations
  const [virtualTanks, setVirtualTanks] = useState(null);
  const [virtualBatches, setVirtualBatches] = useState(null);
  const [virtualLoading, setVirtualLoading] = useState(false);
  const [isVirtualMode, setIsVirtualMode] = useState(false);
  const [protocolSteps, setProtocolSteps] = useState([]);
  const [protocolApplication, setProtocolApplication] = useState(null);
  const [isNextToExecute, setIsNextToExecute] = useState(true);

  // Rollback modal dialog lifecycle
  const rollbackModalRef = useRef(null);
  useEffect(() => {
    const modalElement = rollbackModalRef.current;
    if (!modalElement) return;
    if (isRollbackOpen) {
      modalElement.close();
      modalElement.showModal();
    } else {
      modalElement.close();
    }
  }, [isRollbackOpen]);

  const drugBatchFieldArray = useFieldArray({
    control,
    name: "drug_batches",
  });

  const drugBatchesFieldArrayTmp = useFieldArray({
    control: control,
    name: "drug_batches_tmp",
  });

  const watchedDrugBatches = useWatch({
    control: control,
    name: "drug_batches",
  });

  // Seed the form `drug_batches` from the operation's treatments so the interactive batch_row
  // column (USER / CLIENT-without-USER) shows already-assigned lots as the "pen" state instead of "+".
  useEffect(() => {
    if (!isDrugBatchInteractive(role, auth, operation, users)) return;
    if (!Array.isArray(operation?.treatments) || operation.treatments.length === 0) return;

    const grouped = groupBySubstanceId(operation.treatments);
    grouped.forEach((t) => {
      if ((t?.drug_batches || []).length > 0) {
        setValue(`drug_batches.${t.substance_id}`, t.drug_batches);
      }
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [operation?.id, role]);

  const setModifyMode = (row_id) => {
    setIsModifyMode(row_id);
  };

  // const handleOpenModal = (isModifyId = -1, modalType) => {
  //   setIsBatchSelectorModal(modalType);
  //   setIsModalOpen(true);
  //   setModifyMode(isModifyId);
  // };

  // const handleCloseModal = () => {
  //   if (errors?.day) clearErrors("day");
  //   setIsModalOpen(false);
  // };

  const handleConfirmModal = () => {
    if (isBatchSelectorModal === "BATCH_SELECTOR") {
      const drug = drugs?.find((d) => d?.id === isModifyMode);
      const overriddentQuantity = operation?.treatments?.find(tt => tt?.substance_id === isModifyMode)?.quantity || drug?.quantity;
      
      if (drugBatchesFieldArrayTmp.fields?.length > 0) {
        const batches =
          drugBatchesFieldArrayTmp.fields?.map((b) => {
            return { ...b, substance_id: drug?.id };
          }) || [];

        const usedTotal =
          batches.reduce((sum, b) => sum + (Number(b.quantity) || 0), 0) || 0;

        const warehouseUnit = getWarehouseUnit(drug, getLabel);
        const requiredNumericQty = computeRequiredNumericQty(
          {... drug, quantity: overriddentQuantity },
          totalTreatmentVolume
        );
        const requiredWarehouseQty = computeRequiredWarehouseQty(
          warehouseUnit,
          requiredNumericQty
        );
        const remainingQty = Math.max(requiredWarehouseQty - usedTotal, 0);
        const isEnoughStock = usedTotal >= requiredWarehouseQty;
        batches[0].is_enough_stock = isEnoughStock && remainingQty <= 0;
        
        setIsEnoughStock(isEnoughStock && remainingQty <= 0);
        setValue(`drug_batches.${drug.id}`, batches);
      }
    }

    setIsModalOpen(false);
  };

  const loadUnits = async () => {
    const currentUnits = await getUnits(axiosPrivate);
    dispatch(setUnits(currentUnits));
  };

  const loadOperations = async () => {
    const currentOperations = await getOperations(axiosPrivate);
    dispatch(setOperations(currentOperations));
    // console.log("currentOperations", currentOperations);
    // if (currentOperations && currentOperations?.length > 0 && Object.keys(operation)?.length === 0) {
      // setOperation(
      //   currentOperations?.find((operationsDb) => {
      //     return operationsDb.id === Number(id);
      //   }) || {}
      // );
    // }
  };

  const loadUsers = async () => {
    const currentUsers = await getUsers(axiosPrivate);
    dispatch(setUsers(currentUsers));
  };

  const loadTanks = async () => {
    const currentTanks = await getTanks(activeWinery?.id, axiosPrivate);
    dispatch(setTanks(currentTanks));
  };

  const loadDrugs = async () => {
    const currentDrugs = await getDrugs(axiosPrivate);
    dispatch(setDrugs(currentDrugs));
  };

  const loadExams = async () => {
    const currentExams = await getExams(axiosPrivate);
    dispatch(setExams(currentExams));
  };

  const loadTreatments = async () => {
    const currentTreatments = await getProtocols("treatment", axiosPrivate);
    dispatch(setTreatments(currentTreatments));
  };

  const loadProtocols = async () => {
    const loadedProtocols = await getProtocols("winemaking", axiosPrivate);
    dispatch(setWineMaking(loadedProtocols));
  };

  const loadLots = async () => {
    const currentLots = await getLots(activeWinery?.id, axiosPrivate);
    dispatch(setLots(currentLots));
  };

  const loadMachineries = async () => {
    const currentMachineries = await getWarehouseByType(axiosPrivate, "machineries");
    dispatch(setWarehouse({ type: "machineries", data: currentMachineries }));
  };

  const loadAnalysis = async () => {
    const currentAnalysis = await getProtocols("analysis", axiosPrivate);
    dispatch(setAnalysis(currentAnalysis));
  };

  const loadAnalysisState = async () => {
    if (analysisState !== "NOT_SAMPLED") return;
    if (operation?.completion_date) setAnalysisState("STORED");
    else {
      if (operation?.repetitive) {
        const isSample =
          analysisResults?.[analysisResults?.length - 1]?.results === null;
        if (Number(operation?.relative_id) - Number(operation?.completed) > 1)
          setAnalysisState("NOT_SAMPLED");
        else if (
          isSample &&
          analysisResults?.length >
            operation?.src_tanks?.length * Number(operation?.completed)
        ) {
          const anRes = (await getAnalysisResults([id], axiosPrivate)) || [];
          setAnalysisState("NOT_INSERTED");
        } else setAnalysisState("NOT_SAMPLED");
      } else {
        // console.log('quiii', analysisResults, operation?.src_tanks?.length)
      }
    }
    // console.log(analysisState, 'anStat')
  };

  useEffect(() => {
    if (operation?.type !== 'ANALYSIS') return;
    const tmp = [];
    const temperatureMap = {};

    // Per operazioni ripetitive, includiamo i treatments di TUTTI i cicli.
    // operation.treatments contiene il ciclo corrente; previousTreatments gli altri cicli.
    let analysisArray = [...operation?.treatments || [], ...previousTreatments || []];

    // Assicura che tutti gli elementi abbiano relative_id prima del sort
    analysisArray = analysisArray?.map(an => ({
      ...an,
      relative_id: an?.relative_id || operation?.relative_id
    }));

    const processedTanks = new Set();

    const isOnTank = operation?.on_tank;
    analysisArray
      ?.sort((a, b) => {
        // Prima ordina per relative_id, poi per tank_id
        const relIdA = Number(a?.relative_id) || 0;
        const relIdB = Number(b?.relative_id) || 0;
        if (relIdA !== relIdB) return relIdA - relIdB;
        return (a?.tank_id || 0) - (b?.tank_id || 0);
      });

    
    analysisArray
      ?.forEach((an, i) => {
        const source = operation?.on_tank
          ? tanks?.find((t) => t.id == an?.tank_id)
          : lots?.find((l) => l.id == an?.batch_id);
        // Per operazioni ripetitive (sia complete che incomplete), non mergare elementi di cicli diversi
        const separateByRelativeId = operation?.repetitive;
        const index = tmp?.findIndex((t) =>
          t?.substance_name === an?.substance_name &&
          (isOnTank ? (t.tank_id == an?.tank_id) : (t.batch_id == an?.batch_id)) &&
          (!separateByRelativeId || t?.relative_id === an?.relative_id)
        );
        if (index > -1) {
          const items = tmp[index].items;
          // Se esiste già un item con lo stesso range/unit, accorpa solo il colore
          // invece di duplicare la riga (es. stesso tank con più record dello stesso colore).
          const existingItem = items.find((it) =>
            it?.min_range === an?.min_range &&
            it?.max_range === an?.max_range &&
            it?.unit === an?.unit
          );
          if (existingItem) {
            if (an?.color && !existingItem.colors.includes(an.color)) {
              existingItem.colors.push(an.color);
            }
          } else {
            items.push({ unit: an?.unit, min_range: an?.min_range, max_range: an?.max_range, colors: an?.color ? [an.color] : []});
          }
          tmp[index].items = items
          return true;
        }
        // Per operazioni ripetitive, traccia per ciclo + tank; per non ripetitive, solo tank
        const tankKey = an?.tank_id || an?.batch_id;
        const trackerKey = operation?.repetitive ? `${an?.relative_id}_${tankKey}` : tankKey;
        const isTankFirst = !processedTanks.has(trackerKey);

        if (isTankFirst && an?.temperature) {
          temperatureMap[trackerKey] = an?.temperature;
        }

        tmp.push({
          ...an,
          relative_id: an?.relative_id || operation?.relative_id,
          batch_name: source?.batch_name,
          isTankFirst: isTankFirst, // true only if not already processed
          collection_date: isTankFirst ? an?.collection_date : null,
          _originalCollectionDate: an?.collection_date, // salva il valore originale
          user_id: isTankFirst ? an?.user_id : null,
          _originalUserId: an?.user_id, // salva il valore originale
          src_name: isTankFirst ? source?.name : null,
          _originalSrcName: source?.name, // salva il valore originale
          temperature: an?.temperature,
          enableRange: i < filteredSubstances?.analysis?.length,
          isShowFirstLineData: isTankFirst, //obj.isTankFirst,    // TO DO, if ok remove isShowFirstLineData and use isTankFirst i % rowCount === 0;
          // relative_id = Math.floor(tmpData?.length / (tanksLen * anLen)) + 1;
          // endOfRepBorder: true,// i % rowCount === 0,
          endOfSrcNameBorder: isTankFirst,
          opType: "ANALYSIS",
          items: [{ unit: an?.unit, min_range: an?.min_range, max_range: an?.max_range, colors: an?.color ? [an.color] : []}],
        });

        processedTanks.add(trackerKey); // mark this tank (or batch) for this cycle as processed
      });

      // Ordina per relative_id, poi per tank name, infine per substance name
      tmp.sort((a, b) => {
        // Prima per relative_id
        const relIdA = Number(a?.relative_id) || 0;
        const relIdB = Number(b?.relative_id) || 0;
        if (relIdA !== relIdB) return relIdA - relIdB;

        // Poi per tank/batch name — usa _originalSrcName perché src_name è null sulle righe non-first
        const srcNameA = a?._originalSrcName || '';
        const srcNameB = b?._originalSrcName || '';
        if (srcNameA !== srcNameB) return srcNameA.localeCompare(srcNameB);

        // Infine per substance name
        const substanceA = a?.substance_name || '';
        const substanceB = b?.substance_name || '';
        return substanceA.localeCompare(substanceB);
      });

      // Ricalcola isTankFirst e isShowFirstLineData basandosi sul nuovo ordine dopo il sort
      const processedTanksAfterSort = new Set();
      let previousRelId = null;
      tmp.forEach((row) => {
        const tankKey = row?.tank_id || row?.batch_id;
        const trackerKeyAfterSort = operation?.repetitive ? `${row?.relative_id}_${tankKey}` : tankKey;
        const isTankFirstAfterSort = !processedTanksAfterSort.has(trackerKeyAfterSort);

        row.isTankFirst = isTankFirstAfterSort;
        row.isShowFirstLineData = isTankFirstAfterSort;
        row.collection_date = isTankFirstAfterSort ? row?._originalCollectionDate : null;
        row.user_id = isTankFirstAfterSort ? row?._originalUserId : null;
        row.src_name = isTankFirstAfterSort ? row?._originalSrcName : null;
        row.endOfSrcNameBorder = isTankFirstAfterSort;

        // Marca la prima riga di ogni ciclo (relative_id) per separare visivamente
        // le ripetizioni (es. 1/2 da 2/2) con un border-top più marcato (.rowSpan).
        const relId = Number(row?.relative_id);
        row.endOfRepBorder = !!operation?.repetitive && relId !== previousRelId;
        previousRelId = relId;

        processedTanksAfterSort.add(trackerKeyAfterSort);
      });

      setAnalysisData(tmp);
      setValue('temperature', temperatureMap);
  }, [operation, filteredSubstances, setValue, previousTreatments, tanks, lots]);

  // Per le analisi ripetitive, ogni ciclo (1/4, 2/4, ...) è un'operazione separata collegata
  // da main_id. Carichiamo i treatments di TUTTI gli altri cicli in previousTreatments,
  // indipendentemente dallo stato di completamento, così la tabella mostra tutte le ripetizioni.
  useEffect(() => {
    const loadAllCyclesTreatments = async () => {
      if (operation?.type !== 'ANALYSIS' || !operation?.repetitive || !operation?.id) {
        setPreviousTreatments([]);
        return;
      }

      const main_id = operation?.main_id ? operation?.main_id : operation?.id;
      const relatedOperations = (await getOperationsByMainId(axiosPrivate, main_id)) || [];

      // getOperationsByMainId returns only child operations (main_id = main_id),
      // NOT the main operation itself. When current op is a child, fetch main separately.
      let mainOpTreatments = [];
      if (operation?.main_id) {
        const mainOpArr = (await getOperationById(axiosPrivate, main_id)) || [];
        const mainOp = mainOpArr?.[0];
        if (mainOp?.treatments?.length > 0) {
          mainOpTreatments = mainOp.treatments.map((t) => ({ ...t, relative_id: mainOp?.relative_id }));
        }
      }

      // tutti i cicli tranne quello corrente (i suoi treatments sono già in operation.treatments)
      const otherCyclesTreatments = [
        ...mainOpTreatments,
        ...(relatedOperations
          ?.filter((op) => Number(op?.id) !== Number(operation?.id))
          ?.flatMap((op) =>
            (op?.treatments || []).map((t) => ({ ...t, relative_id: op?.relative_id }))
          ) || []),
      ];

      setPreviousTreatments(otherCyclesTreatments);
    };

    loadAllCyclesTreatments();
  }, [operation?.id, operation?.repetitive, operation?.type, operation?.main_id, axiosPrivate]);


  const customIdInvalidSrcTank = 'custom-id-invalid-src-tank';
  const customIdInvalidAnalysisResults = 'custom-id-invalid-analysis-results';
  const [isInvalidSrcTank, setIsInvalidSrcTank] = useState(false);

  const initData = async () => {
    const promiseArray = [];

    if (operations?.length === 0) {
      promiseArray.push(loadOperations());
    }
    if (users?.length === 0) {
      promiseArray.push(loadUsers());
    }
    if (tanks?.length === 0) {
      promiseArray.push(loadTanks());
    }
    if (lots?.length === 0) {
      promiseArray.push(loadLots());
    }
    if (analysis?.length === 0) {
      promiseArray.push(loadAnalysis());
    }
    if (drugs?.length === 0) {
      promiseArray.push(loadDrugs());
    }
    if (exams?.length === 0) {
      promiseArray.push(loadExams());
    }
    if (treatments?.length === 0) {
      promiseArray.push(loadTreatments());
    }
    if (winemakingProtocols?.length === 0) {
      promiseArray.push(loadProtocols());
    }
    if (units?.length === 0) {
      promiseArray.push(loadUnits());
    }
    if (!machineries || machineries.length === 0) {
      promiseArray.push(loadMachineries());
    }

    return Promise.all(promiseArray);
  };

  useEffect(() => {
    initData();
  }, []);

  useEffect(() => {
    loadCompletedOperations();
  }, [operation?.id]);

  useEffect(() => {
    loadAnalysisState();
  }, [analysisResults, operation?.completion_date]);

  useEffect(() => {
    let totalVolume = 0;
    for (const tank of operation?.src_tanks || []) {
      const currentTank = tanks?.find(tt => tt?.id === tank?.tank_id);
      totalVolume += Number(currentTank?.quantity) || 0;
    }
    
    setTotalTreatmentVolume(totalVolume);
  }, [operation?.src_tanks]);

  useEffect(() => {
    if (["TREATMENT", "ANALYSIS", "CLEANING", "DESTEMMING"].includes(operation?.type)) filterDisabledSubstances();

    isSrcTanksStillInvalid();
  }, [operation, exams, drugs]);

  // Toast per tank vuoto - solo se non in protocol app oppure se è il prossimo step da eseguire
  // Non applicabile a CIP/CLEANING perché i tank sorgente sono sempre vuoti dopo la pulizia
  useEffect(() => {
    if (["CIP", "CLEANING"].includes(operation?.type)) {
      toast.dismiss(customIdInvalidSrcTank);
      setIsInvalidSrcTank(false);
      return;
    }

    const isInvalid = isSrcTanksStillInvalid(operation, lots, tanks);
    const isInProtocolApplication = !!operation?.protocol_application_id;
    let shouldShowToast = false;

    if (isInProtocolApplication && protocolSteps.length > 0) {
      // Se in protocol app, mostra toast solo se è il prossimo step da eseguire
      const currentStep = protocolSteps.find(s => s.id === operation.protocol_step_id);
      const prevSteps = protocolSteps.filter(s => s.step_order < (currentStep?.step_order ?? 0));
      const isNextToExecute = prevSteps.every(s => {
        if (s.execution_status === "EXECUTED") return true;
        if (s.executed_at) return true;
        if ((s.operation || s.operation_type)?.toUpperCase?.() === "ANALYSIS" && s.validation_status !== "NOT_SAMPLED") return true;
        return false;
      });

      if (isInvalid && isNextToExecute) {
        shouldShowToast = true;
        toast(getLabel("toast_srcTankNowEmpty"), {
          toastId: customIdInvalidSrcTank,
          type: toast.TYPE.ERROR,
          isLoading: false,
          position: toast.POSITION.BOTTOM_RIGHT,
        });
      } else {
        toast.dismiss(customIdInvalidSrcTank);
      }
    } else if (!isInProtocolApplication && isInvalid) {
      // Se non in protocol app, mostra toast se i tank sono vuoti
      shouldShowToast = true;
      toast(getLabel("toast_srcTankNowEmpty"), {
        toastId: customIdInvalidSrcTank,
        type: toast.TYPE.ERROR,
        isLoading: false,
        position: toast.POSITION.BOTTOM_RIGHT,
      });
    } else {
      toast.dismiss(customIdInvalidSrcTank);
    }

    setIsInvalidSrcTank(shouldShowToast);
  }, [operation, protocolSteps, lots, tanks]);

  // Calculate if this step is the next to execute in protocol application
  useEffect(() => {
    const isInProtocolApplication = !!operation?.protocol_application_id;
    if (!isInProtocolApplication || protocolSteps.length === 0) {
      setIsNextToExecute(true); // Not in protocol app, allow execution
      return;
    }

    const currentStep = protocolSteps.find(s => s.id === operation.protocol_step_id);
    if (!currentStep) {
      setIsNextToExecute(true); // Step not found, allow execution
      return;
    }

    const prevSteps = protocolSteps.filter(s => s.step_order < (currentStep?.step_order ?? 0));
    const isNext = prevSteps.every(s => {
      if (s.execution_status === "EXECUTED") return true;
      if (s.executed_at) return true;
      // ANALYSIS steps that have been sampled (status != NOT_SAMPLED) are considered as done
      if ((s.operation || s.operation_type)?.toUpperCase?.() === "ANALYSIS" && s.validation_status !== "NOT_SAMPLED") return true;
      return false;
    });
    setIsNextToExecute(isNext);
  }, [operation?.protocol_application_id, operation?.protocol_step_id, protocolSteps]);

  // Check if there's a previous ANALYSIS step that has been sampled
  const hasAnalysisSampled = useMemo(() => {
    if (!operation?.protocol_application_id || protocolSteps.length === 0) return false;

    const currentStep = protocolSteps.find(s => s.id === operation.protocol_step_id);
    if (!currentStep) return false;

    // Find all ANALYSIS steps before this one
    // Note: steps use 'operation' or 'operation_type' property, not 'name'
    const prevAnalysisSteps = protocolSteps.filter(s =>
      s.step_order < (currentStep?.step_order ?? 0) &&
      (s.operation || s.operation_type)?.toUpperCase?.() === "ANALYSIS"
    );

    if (prevAnalysisSteps.length === 0) return false;

    // Check if ANY of the previous ANALYSIS steps have been sampled
    // (validation_status is not "NOT_SAMPLED" means it's been sampled or further processed)
    const result = prevAnalysisSteps.some(step =>
      step.validation_status && step.validation_status !== "NOT_SAMPLED"
    );
    console.log('[hasAnalysisSampled] Result:', result);
    return result;
  }, [operation?.protocol_application_id, operation?.protocol_step_id, protocolSteps]);

  // Fetch virtual state for protocol operations
  useEffect(() => {
    if (!operation?.protocol_step_id || !operation?.protocol_application_id || !activeWinery?.id) {
      setVirtualTanks(null);
      setVirtualBatches(null);
      setProtocolSteps([]);
      setProtocolApplication(null);
      return;
    }

    (async () => {
      try {
        setVirtualLoading(true);

        // 1. Fetch protocol steps to determine if this is the "next to execute" step
        const statusData = await getProtocolApplicationStatus(
          operation.protocol_application_id,
          axiosPrivate
        );
        const steps = statusData?.steps || [];
        setProtocolSteps(steps);
        setProtocolApplication(statusData?.application || null);

        const currentStep = steps.find(s => s.id === operation.protocol_step_id);

        if (!currentStep) {
          console.warn("[virtualState] Current step not found in steps array", {
            stepId: operation.protocol_step_id,
            availableSteps: steps.map(s => ({ id: s.id, operation: s.operation || s.operation_type }))
          });
          setVirtualTanks(null);
          setVirtualBatches(null);
          return;
        }
        const prevSteps = steps.filter(s => s.step_order < (currentStep?.step_order ?? 0));
        const isNextToExecute = prevSteps.every(s => {
          if (s.execution_status === "EXECUTED") return true;
          if (s.executed_at) return true;
          if ((s.operation || s.operation_type)?.toUpperCase?.() === "ANALYSIS" && s.validation_status !== "NOT_SAMPLED") return true;
          return false;
        });

        // Check if there are wine movement operations before this step
        const wineMovementOps = ["DESTEMMING", "RACKING", "DECANT", "FILTERING", "CLOSE", "CUT", "NEW_LOT", "BOTTLING"];
        const hasWineMovementBefore = prevSteps.some(s => {
          const opType = (s.operation || s.operation_type)?.toUpperCase?.();
          return wineMovementOps.includes(opType);
        });

        console.log("[virtualState] Debug:", {
          stepId: operation.protocol_step_id,
          currentStep: currentStep?.operation || currentStep?.operation_type,
          currentStepOrder: currentStep?.step_order,
          prevStepsCount: prevSteps.length,
          prevStepsStatus: prevSteps.map(s => ({ operation: s.operation || s.operation_type, status: s.validation_status })),
          isNextToExecute,
          hasWineMovementBefore
        });

        if (isNextToExecute || !hasWineMovementBefore) {
          // Prossimo step da eseguire oppure nessun wine movement prima: mostra solo stato corrente Redux, nessun toggle
          console.log("[virtualState] Not loading virtual state - reason:", isNextToExecute ? "next to execute" : "no wine movement before");
          setIsVirtualMode(false);
          setVirtualTanks(null);
          setVirtualBatches(null);
          return;
        }

        // Step futuro con wine movement prima: fetcha virtual state e mostra toggle (default: virtuale)
        console.log("[virtualState] Loading virtual state for future step:", operation.protocol_step_id);
        const data = await getStepVirtualState(operation.protocol_step_id, axiosPrivate);
        console.log("[virtualState] Loaded virtual state:", { tanks: data?.virtual_state?.tanks?.length, batches: data?.virtual_state?.batches?.length });
        setVirtualTanks(data?.virtual_state?.tanks || null);
        setVirtualBatches(data?.virtual_state?.batches || null);
        setIsVirtualMode(true);
      } catch (err) {
        console.error("[virtualState] error:", err);
        setVirtualTanks(null);
        setVirtualBatches(null);
      } finally {
        setVirtualLoading(false);
      }
    })();
  }, [operation?.protocol_step_id, operation?.protocol_application_id, activeWinery?.id]);

  // useEffect(() => {
    // if (operations?.length === 0) {
    //   loadOperations();
    // }
    // if (completedOperations?.length === 0) {
    //   loadCompletedOperations();
    // }

    // if (operations && operations?.length > 0 && Object.keys(operation)?.length === 0) {
    //   setOperation(
    //     operations.find((operationsDb) => {
    //       return operationsDb.id === Number(id);
    //     }) || {}
    //   );
    // }

    // if (completedOperations?.length > 0 && Object.keys(operation)?.length === 0) {
    //   setOperation(
    //     completedOperations.find((operationsDb) => {
    //       return operationsDb.id === Number(id);
    //     }) || {}
    //   );
    // }

    // console.log(completedOperations, id)
  // }, [id, completedOperations, operation]);

  const loadCompletedOperations = async () => {
    const singleCompletedOperation = await getOperationById(axiosPrivate, id);

    if (
      !singleCompletedOperation ||
      singleCompletedOperation?.length === 0 ||
      singleCompletedOperation?.[0]?.completion_date === null
    ) {
      if (singleCompletedOperation?.length > 0) {
        // Not completed yet, but the backend found it: make sure it's in the
        // `operations` slice even if that slice was hydrated (and never
        // refreshed since) before this operation existed, otherwise
        // selectOperationByIdMemo never resolves and the page stays blank.
        dispatch(updateOp(singleCompletedOperation[0]));
      }

      const analysisInStore =
        storedAnalysisResults?.filter((stored) => stored.operation_id === id) ||
        [];

      if (analysisInStore?.length === 0) {
        const anRes = (await getAnalysisResults([id], axiosPrivate)) || [];
        const anResults = anRes?.sort((a, b) => a?.id - b?.id);
        if (anResults?.length > 0 && anResults?.[0]?.results !== null) {
          setAnalysisResults(anResults);
          dispatch(addStoredAnalysisResults(anResults));
          // console.log('not found')
        } else if (anResults?.length > 0 && anResults?.[0]?.results === null) {
          setAnalysisState("NOT_INSERTED");
          setAnalysisResults(anResults);
        }
      } else {
        // console.log('found')
        setAnalysisResults(analysisInStore || []);
      }
      return;
    } else if (
      singleCompletedOperation?.[0]?.completion_date !== null

    ) {
      // setCompletedOperations2(singleCompletedOperation);
      // dispatch(setCompletedOperations(singleCompletedOperation));
      dispatch(addCompletedOp(singleCompletedOperation?.[0]));
      if (singleCompletedOperation[0]?.type !== "ANALYSIS" &&
        singleCompletedOperation[0]?.type !== "TREATMENT") return;
    }
    // else if (singleCompletedOperation[0]?.type !== 'ANALYSIS' && singleCompletedOperation[0]?.type !== 'TREATMENT') return;

    let operation_id = null;
    if (singleCompletedOperation?.[0]?.repetitive) {
      // get all the related operations
      const main_id = operation?.main_id ? operation?.main_id : operation?.id;
      const relatedOperations = await getOperationsByMainId(axiosPrivate, main_id);

      // NB: il caricamento dei treatments di tutti i cicli (previousTreatments) è gestito
      // dall'effect dedicato loadAllCyclesTreatments, qui ricaviamo solo operation_id.

      const isMain = singleCompletedOperation[0]?.main_id === null;
      operation_id = isMain
        ? id
        : relatedOperations?.find((o) => o.id == id)?.main_id;
      const singleOp =
        (isMain
          ? relatedOperations?.filter((o) => o.id == id)
          : singleCompletedOperation) || null;
      // if (singleOp !== null) setCompletedOperations2(singleOp);
    } else {
      operation_id = singleCompletedOperation[0]?.id;
      // if (operation_id) setCompletedOperations2(singleCompletedOperation);
      // if (Object.keys(operation)?.length === 0) setOperation(singleCompletedOperation?.[0] || {});   // TO DO se la decommento non ottengo src_tanks e dest_tanks
    }

    const analysisInStore =
      operation_id === null
        ? []
        : storedAnalysisResults?.filter(
            (stored) =>
              stored.operation_id === id ||
              stored.operation_id === operation_id
          ) || [];
    // console.log('analysisInStore', analysisInStore)
    // console.log('storedAnalysisResults', storedAnalysisResults)

    if (analysisInStore?.length === 0) {
      const anRes =
        (await getAnalysisResults([operation_id], axiosPrivate)) || [];
      const anResults = anRes?.sort((a, b) => a?.id - b?.id);

      if (anResults?.length > 0 && anResults?.[0]?.results !== null) {
        setAnalysisResults(anResults);
        dispatch(addStoredAnalysisResults(anResults));
      //   console.log('not found 2', anResults)
      } else if (anResults?.length > 0 && anResults?.[0]?.results === null) {
        // console.log('not found 2', anResults)
        if (anResults?.[0]?.collection_date === null) {
          setAnalysisState("NOT_SAMPLED");
        } else {
          setAnalysisState("NOT_INSERTED");
        }
      }
    } else {
      // console.log('found 2')
      setAnalysisResults(analysisInStore || []);
    }
  };

  const filterDisabledSubstances = () => {
    if (type === 'CLEANING') {
      const ops = operation?.treatments || [];
      const name = winemakingProtocols?.find(t => operation?.winemaking_protocol_id == t?.id)?.name || "";
      const tmp = { name: name, operations: [...ops]?.sort((a, b) => a?.day - b?.day) }
      setFilteredSubstances(tmp);
      return tmp;
    } else {
      const isTreatment = type === 'TREATMENT' || type === 'DESTEMMING'; 
      const data = isTreatment ? treatments : analysis;
      const tmp = { ...(data?.find(
        (a) => a.protocols_id == operation?.protocol_id
      ) || {})};
      if ((type === 'ANALYSIS' && !tmp?.analysis) || (isTreatment && !tmp?.drugs)) return {};
    
      const tmpArray = [];

      const srcArr = Array.isArray(
        type === 'ANALYSIS' ? tmp?.analysis :
        tmp?.drugs
      ) ? [
        ...(type === 'ANALYSIS' ? tmp?.analysis :
          tmp?.drugs)
      ] : [];

      const srcSubstancesArr = Array.isArray(type === 'ANALYSIS' ? exams : drugs)
        ? [...(type === 'ANALYSIS' ? exams : drugs)]
        : [];
      if (Array.isArray(operation?.treatments) && operation?.treatments?.length > 0 && srcArr?.length > 0) {
        // substance_id that have been disable for this application
        const onlyRemoved = srcSubstancesArr
        ?.filter((s) => operation?.treatments?.some((tt) => tt?.substance_id === s?.id && tt?.removed === true))
        ?.map((s) => { return {
          ...s,
          substance_id: s?.id,
          substance_name: s?.name,
          removed: true,
        }});
        const onlyNotRemoved = srcSubstancesArr
        ?.filter((s) => operation?.treatments?.some((tt) => tt?.substance_id === s?.id && (!tt?.removed || tt?.removed === false)))
        ?.map((s) => { return {
          ...s,
          substance_id: s?.id,
          substance_name: s?.name,
          // drug_batch_number: operation?.treatments?.find((tt) => tt?.substance_id === s?.id && (!tt?.removed || tt?.removed === false))?.drug_batch_number || ""
          ...(operation?.treatments?.find((tt) => tt?.substance_id === s?.id && (!tt?.removed || tt?.removed === false)))
        }});
      
        tmpArray.push(...onlyRemoved, ...onlyNotRemoved);
      }
      
      if (type === 'ANALYSIS') {
        tmp.analysis = operation?.treatments?.length > 0 ? tmpArray : srcArr;
      } else {
        tmp.drugs = tmpArray;
      }
    
      setFilteredSubstances(tmp);

      return tmp;
    }
  }

  const addChatNote = (newNote) => {
    if (!operation?.id) return;
  
    const updatedChatNotes = Array.isArray(operation.chat_notes)
      ? [...operation.chat_notes, newNote]
      : [newNote];
  
    // Assicura che timestamp sia una stringa ISO (serializzabile)
    const serializedNotes = updatedChatNotes.map((note) => ({
      ...note,
      timestamp: typeof note.timestamp === 'string'
        ? note.timestamp
        : new Date(note.timestamp).toISOString(),
    }));

    dispatch(updateNote({
      id: operation.id,
      chat_notes: serializedNotes,
    }));
  };

  const getInfoByType = (type) => {
    switch (type) {
      case "NEW_LOT":
      case "DESTEMMING":
        return (
          <div className="operation_destemming_wrapper">
            <DestemmingDetail
              operation={operation}
              treatments={filteredSubstances}
              users={users}
              tanks={tanks}
              lots={lots}
              drugs={drugs}
              control={control}
              completed={completed}
              drops={drops}
              // batchData={lots?.find?.((lot) => {
              //   return Number(lot?.id) === Number(operation?.batch_id_in);
              // })}
              // batchData={operation?.completion_date === null ? operation?.src_tanks?.[0]?.preOperation?.batch : operation?.src_tanks?.[0]?.postOperation?.batch}
              configs={configs}
              register={register}
              errors={errors}
              getValues={getValues}
              setValue={setValue}
              clearErrors={clearErrors}
              axiosPrivate={axiosPrivate}
              dispatch={dispatch}
              user_id={auth?.user_id}
              isNewLot={type === "NEW_LOT"}
              isMobile={isMobile}
              handleOpenModal={handleOpenModal}
              watchedDrugBatches={watchedDrugBatches}
              isVirtualMode={isVirtualMode && !!virtualTanks && !isNextToExecute}
              virtualTanks={virtualTanks}
              isNextToExecute={isNextToExecute}
              totalActualDest={totalActualDest}
              protocolApplicationBanner={
                operation?.protocol_application_id ? (
                  <ProtocolApplicationBanner
                    protocolApplicationId={operation.protocol_application_id}
                    protocolStepId={operation.protocol_step_id}
                    protocolSteps={protocolSteps}
                  />
                ) : null
              }
            />
          </div>
        );

      case "CUT":
      case "CLOSING":
      case "DECANT":
      case "RACKING":
      case "FILTERING":
      case "OUILLAGE":
        return (
          <div className="operation_cut_wrapper">
            <CutDetail
              operation={operation}
              completed={completed}
              drops={drops}
              users={users}
              lots={lots}
              tanks={tanks}
              configs={configs}
              type={type}
              register={register}
              errors={errors}
              getValues={getValues}
              setValue={setValue}
              clearErrors={clearErrors}
              axiosPrivate={axiosPrivate}
              dispatch={dispatch}
              user_id={auth?.user_id}
              isMobile={isMobile}
              isVirtualMode={isVirtualMode && !!virtualTanks && !isNextToExecute}
              virtualTanks={virtualTanks}
              totalActualDest={totalActualDest}
              control={control}
              protocolApplicationBanner={
                operation?.protocol_application_id ? (
                  <ProtocolApplicationBanner
                    protocolApplicationId={operation.protocol_application_id}
                    protocolStepId={operation.protocol_step_id}
                    protocolSteps={protocolSteps}
                  />
                ) : null
              }
            />
          </div>
        );
      case "TREATMENT":
      case "CLEANING":
        return (
          <div className="operation_treatment_wrapper">
            <TreatmentDetail
              type={type}
              operation={operation}
              completed={completed}
              register={register}
              errors={errors}
              users={users}
              tanks={tanks}
              lots={lots}
              drugs={drugs}
              control={control}
              treatments={filteredSubstances}
              configs={configs}
              getValues={getValues}
              setValue={setValue}
              axiosPrivate={axiosPrivate}
              dispatch={dispatch}
              user_id={auth?.user_id}
              isMobile={isMobile}
              handleOpenModal={handleOpenModal}
              watchedDrugBatches={watchedDrugBatches}
              isVirtualMode={isVirtualMode && !!virtualTanks && !isNextToExecute}
              virtualTanks={virtualTanks}
              isNextToExecute={isNextToExecute}
              protocolApplicationBanner={
                operation?.protocol_application_id ? (
                  <ProtocolApplicationBanner
                    protocolApplicationId={operation.protocol_application_id}
                    protocolStepId={operation.protocol_step_id}
                    protocolSteps={protocolSteps}
                  />
                ) : null
              }
            />
          </div>
        );
      case "ANALYSIS":
        return (
          <div className="operation_analysis_wrapper">
            <AnalysisDetail
              data={analysisData}
              operation={operation}
              register={register}
              users={users}
              tanks={tanks}
              lots={lots}
              configs={configs}
              results={analysisResults?.sort((a, b) => a.id - b.id)}
              analysis={filteredSubstances}
              isCellarmanEnabled={
                isCellarmanEnabled &&
                !(
                  operation?.repetitive &&
                  Number(operation?.completed) <
                    Number(operation?.relative_id) - 1
                )
              }
              registerComplete={register}
              errorsComplete={errors}
              controlComplete={control}
              analysisState={analysisState}
              getValues={getValues}
              setValue={setValue}
              axiosPrivate={axiosPrivate}
              dispatch={dispatch}
              user_id={auth?.user_id}
              isMobile={isMobile}
              previousTreatments={previousTreatments}
              virtualBatches={virtualBatches}
              isVirtualMode={isVirtualMode && !!virtualTanks && !isNextToExecute}
              protocolApplication={protocolApplication}
              protocolApplicationBanner={
                operation?.protocol_application_id ? (
                  <ProtocolApplicationBanner
                    protocolApplicationId={operation.protocol_application_id}
                    protocolStepId={operation.protocol_step_id}
                    protocolSteps={protocolSteps}
                  />
                ) : null
              }
            />
          </div>
        );
      case "ADD":
      case "ENZYME":
      case "SO2":
      case "HOMOGENIZATION_MASS":
      case "NITROGEN":
      case "INOCULATION":
      case "BOIS":
      case "TANNISSAGE":
      case "OXYGENATION":
      case "STABILIZATION_STATIC":
      case "STABILIZATION_DYNAMIC":
      case "STABILIZATION_RESIN":
        return (
          <div className="operation_automatic_wrapper">
            <AutomaticDetail
              type={type}
              operation={operation}
              completed={completed}
              users={users}
              tanks={tanks}
              lots={lots}
              drugs={drugs}
              control={control}
              configs={configs}
              register={register}
              errors={errors}
              getValues={getValues}
              setValue={setValue}
              axiosPrivate={axiosPrivate}
              dispatch={dispatch}
              user_id={auth?.user_id}
              isMobile={isMobile}
              handleOpenModal={handleOpenModal}
              watchedDrugBatches={watchedDrugBatches}
              isVirtualMode={isVirtualMode && !!virtualTanks && !isNextToExecute}
              virtualTanks={virtualTanks}
              isNextToExecute={isNextToExecute}
              protocolApplicationBanner={
                operation?.protocol_application_id ? (
                  <ProtocolApplicationBanner
                    protocolApplicationId={operation.protocol_application_id}
                    protocolStepId={operation.protocol_step_id}
                    protocolSteps={protocolSteps}
                  />
                ) : null
              }
            />
          </div>
        );
      case "BOTTLING":
        return (
          <div className="operation_bottling_wrapper">
            <BottlingDetail
              operation={operation}
              completed={completed}
              drops={drops}
              users={users}
              lots={lots}
              tanks={tanks}
              configs={configs}
              drugs={drugs}
              control={control}
              register={register}
              errors={errors}
              getValues={getValues}
              setValue={setValue}
              axiosPrivate={axiosPrivate}
              dispatch={dispatch}
              user_id={auth?.user_id}
              isMobile={isMobile}
              handleOpenModal={handleOpenModal}
              watchedDrugBatches={watchedDrugBatches}
              isVirtualMode={isVirtualMode && !!virtualTanks && !isNextToExecute}
              virtualTanks={virtualTanks}
              isNextToExecute={isNextToExecute}
              protocolApplicationBanner={
                operation?.protocol_application_id ? (
                  <ProtocolApplicationBanner
                    protocolApplicationId={operation.protocol_application_id}
                    protocolStepId={operation.protocol_step_id}
                    protocolSteps={protocolSteps}
                  />
                ) : null
              }
            />
          </div>
        );
      default:
        return (
          <div className="operation_error">{getLabel("genericError")}</div>
        );
    }
  };

  const requestPDF = async () => {
    const data = {
      type: "operationDetail",
      title: getLabel("operationDetail"),
      rows: operations?.map((operation) => operation.id),
      columns: [
        getLabel("priority"),
        getLabel("operationType"),
        getLabel("operationSrcTank"),
        getLabel("operationDestTank"),
        getLabel("cellarman"),
        getLabel("expiringDate"),
        getLabel("operationNotes"),
      ],
      headers: [
        "priority",
        "type",
        "src_tanks",
        "dest_tanks",
        "cellarman_ids",
        "expire_date",
        "note",
      ],
      winery_name: activeWinery?.name || "",
      order_by: "expire_date",
      order_sort: "desc",
      timeZone: Intl.DateTimeFormat().resolvedOptions().timeZone,
      language:
        navigator.languages?.length > 0
          ? navigator.languages[0]?.replace("_", "-")
          : "it-IT",
    };

    // await getPDF(data, axiosPrivate);
  };

  const requestLabelPDF = async () => {
    const data = {
      pdf_type: "analysisLabel",
      type: "operations",
      additionalData: {
        subtitle: getLabel("analysisLabel"),
      },
      title: `${activeWinery?.name || ""} AN-${operation?.id}`,
      rows: [[operation?.id]],
      columns: [
        getLabel("analysisName"),
        getLabel("unit"),
        getLabel("substanceRange"),
      ],
      headers: ["analysis_name", "unit", "analysis_min"],
      winery_name: activeWinery?.name || "",
      order_by: "expire_date",
      order_sort: "desc",
      timeZone: Intl.DateTimeFormat().resolvedOptions().timeZone,
      language:
        navigator.languages?.length > 0
          ? navigator.languages[0]?.replace("_", "-")
          : "it-IT",
    };

    await getPDF(
      data,
      axiosPrivate,
      getLabel("pdfType_ANALYSIS") + (operation?.id ? `-${operation?.id}` : "")
    );
  };

  const handleOpenModal = (isModifyId = -1, modalType) => {
    setIsBatchSelectorModal(modalType);
    setIsModalOpen(true);
    setModifyMode(isModifyId);

    if (modalType === "BATCH_SELECTOR") {
      if (isModifyId === -1) {
        reset({
          drug_batches_tmp: [],
        });
      } else if (isModifyId > 0) {
        const thisOpDrugBatches = (operation?.treatments || [])
          .filter(t => t?.substance_id === Number(isModifyId))
          .flatMap(t => t?.drug_batches || [])
          .reduce((acc, batch) => {
            const key = batch.batch_number;

            if (!acc[key]) {
              acc[key] = { ...batch };
            } else {
              acc[key].quantity += batch.quantity;
            }

            return acc;
          }, {});

        // array finale
        const mergedDrugBatches = Object.values(thisOpDrugBatches);

        const drugBatches = getValues(`drug_batches.${isModifyId}`) || [];

        const drug = getDrug();
        const unit = units?.find((u) => u.id === drug?.unit_id)?.unit || "";

        const actualDrugBatches = (drugBatches?.[isModifyId] || [])?.length === 0 ? mergedDrugBatches : drugBatches?.[isModifyId] || [];
        const opQuantity =
          operation?.treatments?.find((t) => t?.substance_id === isModifyMode)
            ?.quantity ||  0;
        const substance = {
          ...drug,
          unit: unit,
          substance_id: drug?.id,
          drug_batches: actualDrugBatches,
          quantity: opQuantity,
        };

        const groupedTreatmentsBySubstanceId = groupBySubstanceId([substance]); // trick to use operation?.treatments
        const d = groupedTreatmentsBySubstanceId?.[0] || null;
        const drugBatchesArray = (d?.drug_batches || []);
        
        if (d) {
          setValue("drug_batches_tmp", drugBatchesArray);
        }
      }
    } else {
      setIsOpen(true);
    }
  };

  const handleManualAddOpenModal = () => {
    setIsManual(true);
    handleOpenModal();
  };

  const handleManualAddConfirmModal = () => {
    setIsManual(false);
    handleCloseModal();
    sendData();
  };

  const handleCloseModal = () => {
    setIsOpen(false);
    setIsModalOpen(false);
    setStep(0);
    setIsBatchSelectorModal(null);

    if (isManual) setIsManual(false);
  };

  const handleImportConfirmModal = () => {
    if (file === null) {
      setError("file", {
        type: "manual",
        message: getLabel("errorOperationExcelFileImport"),
      });
      return;
    } else {
      if (
        file.type !=
        "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet"
      ) {
        setError("file", {
          type: "filetype",
          message: getLabel("errorOperationExcelFileImportFormat"),
        });
        return;
      }
    }

    try {
      importExcel();
    } catch (err) {
      console.log(err);
      setError("file", {
        type: "filetype",
        message: getLabel("errorOperationExcelFileImportError"),
      });
      return;
    }

    if (isImport) {
      setAnalysisState("INSERTING");
      setIsImport(false);
    }
    handleCloseModal();
  };

  const handleFormSubmit = async () => {
    await removeOperation();
    handleCloseModal();
  };

  const importExcel = () => {
    if (file) {
      const reader = new FileReader();
      const analysisExams = operation?.treatments || [];
  
      reader.onload = (e) => {
        const arrayBuffer = e.target.result;
        const workbook = XLSX.read(arrayBuffer, { type: "array" }); // <- updated type
        const sheetName = workbook.SheetNames[0];
        const worksheet = workbook.Sheets[sheetName];
        const json = XLSX.utils.sheet_to_json(worksheet);
  
        const analysisLen = analysisExams?.length - operation?.treatments?.length;
  
        const columnStartIndex =
          !isImport || !operation?.repetitive
            ? 0
            : analysisLen *
              (operation?.src_tanks?.length || 1) *
              (Number(operation?.completed) || 1);
  
        const tankId = getLabel("tank_id");
        const batchId = getLabel("batch_id");
        const substanceId = getLabel("analysisSubstanceId");
        const value = getLabel("examResult");
  
        json?.forEach((row, i) => {
          const tank_id = row?.[tankId];
          const batch_id = row?.[batchId];
          const substance_id = row?.[substanceId];
          const measured_value = row?.[value];
        
          if (!measured_value) return;
        
          
          const indexInData = analysisData?.findIndex(
            (ad) =>
              ad?.batch_id === batch_id &&
              (!operation?.on_tank || ad?.tank_id === tank_id)
          );
          const indexOffset = indexInData + columnStartIndex;
        
          // Set value in "column"
          setValue(`column.${indexOffset}.value`, measured_value);
        
          // Set full tank row
          setValue(`column.tanks.${indexOffset}`, {
            substance_id: substance_id ?? "",
            tank_id: tank_id ?? "",
            batch_id: batch_id ?? "",
            value: measured_value,
          });
        });        
      };
  
      reader.readAsArrayBuffer(file);
    }
  };  

  const customIdDeleteOp = "custom-id-delete-op";
  const removeOperation = async () => {

    const response = await toastAxios(
      () => deleteOperation(
        operation?.main_id || id,
        axiosPrivate
      ),
      customIdDeleteOp,
      { type: operation?.type || "" },
      getLabel,
    );

    console.log('response', response);
    if (response && response?.success) {
      const idsToDelete = response?.deleted_operation_ids || [Number(id)];
      idsToDelete.forEach(deleteId => {
        dispatch(removeOp({ id: deleteId }));
        dispatch(removeCompletedOp({ id: deleteId }));
        dispatch(removeExpOp({ id: deleteId }));
      });
      navigate(`/programming`);
    }
  };

  const customIdRollbackOp = "custom-id-rollback-op";

  // Apre il modale di rollback e carica l'anteprima dell'impatto (catena di
  // operazioni dipendenti che verranno eliminate a cascata).
  const openRollbackModal = async () => {
    setIsRollbackOpen(true);
    setRollbackImpact(null);
    setRollbackImpactLoading(true);
    try {
      const impact = await getRollbackImpact(id, axiosPrivate);
      setRollbackImpact(impact);
    } catch (e) {
      console.error("[rollback] impact error:", e);
      setRollbackImpact(null);
    } finally {
      setRollbackImpactLoading(false);
    }
  };

  const closeRollbackModal = () => {
    if (isRollingBack) return;
    setIsRollbackOpen(false);
    setRollbackImpact(null);
  };

  const performRollback = async () => {
    setIsRollingBack(true);
    const response = await toastAxios(
      () => rollbackOperation(id, {
        username: auth?.username || "",
        client_name: activeWinery?.client_name || "",
      }, axiosPrivate),
      customIdRollbackOp,
      { type: operation?.type || "" },
      getLabel,
    );

    if (response && response?.success) {
      // Rimozione ottimistica del target e delle dipendenti eliminate a cascata.
      // removeOp serve per le operazioni ancora pendenti nella programmazione
      // (es. le ripetizioni collegate di un'analisi 2/4 che vengono eliminate
      // tutte insieme); removeCompletedOp per quelle già completate.
      const dependentIds = (rollbackImpact?.dependents || []).map((d) => Number(d?.id));
      [Number(id), ...dependentIds].forEach((opId) => {
        dispatch(removeOp({ id: opId }));
        dispatch(removeCompletedOp({ id: opId }));
        dispatch(removeExpOp({ id: opId }));
      });
      // Refetch della lista operazioni: il target ri-compare come SCHEDULED e le
      // eventuali altre dipendenti eliminate lato server vengono allineate.
      await loadOperations();
      // Lo stato di tank/batch è stato ripristinato lato server: svuota le cache.
      dispatch(emptyTanks());
      dispatch(emptyLots());
      navigate(`/programming`);
    }
    setIsRollingBack(false);
    setIsRollbackOpen(false);
    setRollbackImpact(null);
  };

  const getDrug = () => {
    const tmp = drugs?.find((d) => d?.id === Number(isModifyMode)) || {};
    const opQuantity =
      operation?.treatments?.find((t) => t?.substance_id === isModifyMode)
        ?.quantity ||  0;

    const drug = {
      ...tmp,
      substance_name: tmp?.name,
      quantity: opQuantity,
    };
    
    return drug;
  };

  const renderDrugBatchModal = () => {
    
    if (isBatchSelectorModal === "BATCH_SELECTOR") return (
        <ConfirmationModal
          isOpen={isModalOpen}
          onConfirm={handleConfirmModal}
          onClose={handleCloseModal}
          description={getLabel("modalConfigureDrugBatchDescription")}
          isOperationRecap={true}
          errors={errors}
          getValues={getValues}
          totalStep={0}
          // errors={modalErrors}
          // getValues={modalGetValues}
          // configs={configs}
        >
          <OperationRecap
            id={operation?.completion_date ? id : null}  // trick to execute automatic assignDrugBatches when op is not completed
            type={"NEW_DRUG_BATCH"}
            configs={configs}
            control={control}
            register={register}
            getValues={register}
            setValue={setValue}
            errors={errors}
            // control={modalControl}
            // register={modalRegister}
            // getValues={modalGetValues}
            // setValue={modalSetValue}
            // errors={modalErrors}
            // units={units}
            arrayFields={drugBatchesFieldArrayTmp}
            additionalData={getDrug()}
            additionalSubtype={totalTreatmentVolume}
            isAutomaticCTA={true}
          />
        </ConfirmationModal>
      );
  }

  const renderDetail = () => {
    const infoComponent = getInfoByType(effectiveType);
    return <div className="operation_info_container">{infoComponent}</div>;
  };

  const buildAnalysisPayload = (data, requestPayload) => {
    let isValid = true;
    const analysisData = [];
    const results = data?.column?.tanks || [];

    if (results?.length === 0 && analysisState !== "NOT_SAMPLED") return [];

    const tanksToProcess = operation?.src_tanks || [];
    const tempData = data?.temperature || {};
    const currentRelId = String(operation?.relative_id ?? "");

    // Le chiavi di temperature possono essere `${tankId}` (input dell'utente) oppure
    // `${relative_id}_${tankId}` (caricate dai cicli precedenti per le analisi ripetitive).
    // Normalizziamo a { numId, relId, value }, tenendo solo i valori effettivamente inseriti.
    const tempEntries = Object.keys(tempData)
      .map((k) => {
        const parts = String(k).split("_");
        return {
          numId: Number(parts[parts.length - 1]),
          relId: parts.length > 1 ? parts[0] : null,
          value: tempData[k],
        };
      })
      .filter((e) => e.value !== undefined && e.value !== null && e.value !== "");

    // Processiamo ogni tank/batch UNA sola volta (dedup per numId), unendo le due fonti:
    // - src_tanks: tank del ciclo corrente (presenti in inserimento risultati);
    // - temperature: tank campionati (presenti in fase di campionamento, quando src_tanks
    //   può non bastare). L'unione+dedup evita sia il doppio processamento (chiavi di più
    //   cicli con lo stesso numId) sia il caso in cui una sola delle due fonti sia popolata.
    // operation.on_tank determina se l'id è un tank_id o un batch_id.
    const idsFromSrc = tanksToProcess.map((t) => (operation?.on_tank ? t?.tank_id : t?.batch_id));
    const idsFromTemp = tempEntries.map((e) => e.numId);
    const idsToProcess = [...new Set([...idsFromSrc, ...idsFromTemp].filter(Boolean))];

    idsToProcess.forEach((rawId) => {
      const numId = Number(rawId);
      if (!numId && analysisState === "NOT_SAMPLED") return true;    // continue
      else if (!numId && analysisState !== "NOT_SAMPLED") return [];

      // Temperatura del tank: preferisci l'input utente (chiave non prefissata), poi il
      // ciclo corrente, infine una qualsiasi per quel tank.
      const tempEntry =
        tempEntries.find((e) => e.numId === numId && e.relId === null) ||
        tempEntries.find((e) => e.numId === numId && e.relId === currentRelId) ||
        tempEntries.find((e) => e.numId === numId);

      // numId è già batch_id o tank_id; operation?.on_tank determina quale sia
      const obj = {
        batch_id: operation?.on_tank ? null : numId,
        tank_id: operation?.on_tank ? numId : null,
        analyst: data?.analyst,
        temperature: Number(tempEntry?.value) || null,
      };

      if (analysisState !== "NOT_SAMPLED") {

        const currentTankAnalysisData = results?.filter(t => operation?.on_tank ? (t?.tank_id == numId) : (t?.batch_id == numId));
        const perTankTreatments = operation?.treatments?.filter(t => operation?.on_tank ? t?.tank_id === numId : t?.batch_id === numId);
        // operation.treatments può contenere lo stesso substance_id duplicato su più cicli:
        // conta le sostanze UNICHE, non la lunghezza grezza.
        const expectedSubstanceCount = perTankTreatments?.length > 0
          ? new Set(perTankTreatments.map(t => t?.substance_id)).size
          : filteredSubstances?.analysis?.length;
        if (currentTankAnalysisData?.length === 0 ||
          currentTankAnalysisData?.length !== expectedSubstanceCount ||
          currentTankAnalysisData?.filter(item => Number(item?.value) === 0)?.length > 0) {

            // Cerca il nome del tank/batch
            let tankName = numId;
            if (operation?.on_tank) {
              tankName = tanks?.find(t => t.id === numId)?.name || numId;
            } else {
              tankName = lots?.find(l => l.id === numId)?.name || lots?.find(l => l.id === numId)?.name || numId;
            }
            toast.error(getLabel(operation?.on_tank ? "toast_analysisMissingPerTank" : "toast_analysisMissingPerBatch", {name: tankName}), {
              toastId: customIdInvalidAnalysisResults,
              isLoading: false,
              position: toast.POSITION.BOTTOM_RIGHT,
              autoClose: 4000,
          });
            isValid = false;
            return false;
          };
          obj.results = currentTankAnalysisData;
        }

      analysisData.push(obj);
    });
    requestPayload.analysis = analysisData;
    return isValid;
  };

  const mergeDrugBatchesById = (drugBatches = [], originalTreatment = {}) => {
    const map = new Map();

    for (const b of drugBatches) {
      if (!b?.drug_batch_id) continue;

      if (!map.has(b.drug_batch_id)) {
        map.set(b.drug_batch_id, {
          ...b,
          unit: originalTreatment.unit,
          unit_id: originalTreatment.unit_id,
          quantity: Number(b.quantity) || 0,
        });
      } else {
        const existing = map.get(b.drug_batch_id);
        existing.quantity += Number(b.quantity) || 0;
      }
    }

    return Array.from(map.values());
  };

  const buildDestemmingPayload = (type, data, requestPayload) => {
    const drops = [];
    const inputUnit = getInputTankUnit();

    if (data?.actual && typeof data.actual === 'object') {
      Object.entries(data.actual).forEach(([v, actualVal]) => {
        if (actualVal === undefined || actualVal === '') return;
        const destTank = operation?.dest_tanks?.find(t =>
          String(t.tank_id) === v || String(t.transfer_id) === v || String(t.close_id) === v
        );
        const plannedL = Number(destTank?.arrow_quantity ?? 0);
        const actualL = inputToLiters(actualVal, inputUnit);
        const dropQty = plannedL - actualL; // positivo = perdita, negativo = guadagno
        if (dropQty === 0) return;
        const tmp = { tank_id: Number(v), quantity: dropQty };
        if (type === 'BOTTLING') {
          delete tmp.tank_id;
          tmp.transfer_id = Number(v);
        } else if (type === 'CLOSING') {
          delete tmp.tank_id;
          tmp.close_id = Number(v);
        }
        drops.push(tmp);
      });
    }

    requestPayload.drop = drops;

    // pomace overrides (DESTEMMING / RACKING)
    if (data?.cellarman_pomaces && typeof data.cellarman_pomaces === 'object') {
      requestPayload.cellarman_pomaces = Object.entries(data.cellarman_pomaces)
        .filter(([_, qty]) => qty !== '' && qty !== null && qty !== undefined)
        .map(([waste_type, qty]) => ({ waste_type, quantity: inputToLiters(qty, inputUnit) }));
    }
    
    const newTreatments = [];
    const groupedTreatmentsBySubstanceId =
      groupBySubstanceId(operation?.treatments) || [];

    for (const tr of groupedTreatmentsBySubstanceId) {
      const overriddenDrugBatches = watchedDrugBatches?.[tr?.substance_id] || [];
      const tmp = { ...tr };

      const originalTreatment =
        operation?.treatments?.find(
          (ot) => ot?.substance_id == tr?.substance_id
        ) || {};

      // console.log('originalTreatment', originalTreatment)

      if (originalTreatment?.quantity) tmp.quantity = originalTreatment?.quantity;

      if (overriddenDrugBatches.length > 0) {
        // const batches = drugs?.find(d => d?.id == tr?.substance_id)?.batches || [];
        
        // ✅ override manuale → usa quelli del cantiniere
        tmp.drug_batches = overriddenDrugBatches.map((bb) => ({
          ...bb,
          id: originalTreatment?.id,
          unit: originalTreatment.unit,
          unit_id: originalTreatment.unit_id,
          // quantity: originalTreatment.quantity,
          // stock: batches?.find(d => d?.drug_batch_id == bb?.drug_batch_id)?.stock || 0,
        }));
      } else {
        // ✅ nessun override → merge automatico
        tmp.drug_batches = mergeDrugBatchesById(
          tr.drug_batches,
          originalTreatment
        );
      }
      newTreatments.push(tmp);
    }
    requestPayload.cellarman_drug_batches = newTreatments;
  };

  const customIdConfirmOp = "custom-id-confirm-op";
  const sendData = async () => {
    const data = getValues();
    // console.log(data, "dataSend", errors);
    // return;
    const requestPayload = {};
    if (operation?.type === "ANALYSIS") {
      
      const errorsNoWarning = (Array.isArray(errors?.column) ? errors.column : []).filter(e => e?.value?.type !== "min" && e?.value?.type !== "max").length;  // exclude min/max warnings
      if (errorsNoWarning > 0) return;
        if (!buildAnalysisPayload(data, requestPayload)) {
          console.log('error buildAnalysisPayload')
          return;
        }
    } else if (
      ["DESTEMMING", "CUT", "DECANT", "RACKING", "NEW_LOT", "BOTTLING", "CLOSING", "TREATMENT", "OUILLAGE"].includes(
        operation?.type
      ) || (operation?.type === "FILTERING" && operation?.start_date)   // FILTERING may have a drop
    ) {
      if (Object.keys(errors)?.length > 0) return;
      buildDestemmingPayload(type, data, requestPayload);

      // Over-allocation guard: without pomaces, the total actually received can
      // never exceed the total planned withdrawal from the source(s) — a "gain"
      // only makes physical sense for DESTEMMING/RACKING when pomace estimates
      // shrink. Applies to every type with this ceiling: DESTEMMING, RACKING,
      // DECANT (1 source -> N destinations) and CUT (N sources -> 1 destination).
      // `drops` and `completed` are live useWatch values in closure scope.
      if (["DESTEMMING", "RACKING", "DECANT", "CUT", "OUILLAGE"].includes(operation?.type)) {
        const _inputUnit = getInputTankUnit();
        // Ceiling: sum of all physical source tanks (a single value for
        // DESTEMMING/RACKING/DECANT, potentially several for CUT).
        // OUILLAGE: src_tanks[].arrow_quantity is frozen at scheduling time
        // and doesn't reflect what other pending ops on the same source may
        // have reserved/released since -- use the live tank's
        // planned_quantity (projected quantity net of ALL outgoing planned
        // transfers) plus this op's own reservation added back, same as
        // Generic/index.js's validateActualQty.
        const _sourceQty =
          operation?.type === "OUILLAGE"
            ? (() => {
                const _srcLiveTank = tanks?.find(
                  (t) => Number(t?.id) === Number(operation?.src_tanks?.[0]?.tank_id)
                );
                const _myReservation =
                  Number(
                    operation?.src_tanks?.[0]?.arrow_quantity ??
                      operation?.src_tanks?.[0]?.old_quantity ??
                      operation?.src_tanks?.[0]?.quantity
                  ) || 0;
                return Math.max(
                  0,
                  (Number(_srcLiveTank?.planned_quantity) || 0) + _myReservation
                );
              })()
            : (operation?.src_tanks || []).reduce(
                (acc, st) => acc + (Number(st?.arrow_quantity ?? st?.old_quantity ?? st?.quantity) || 0),
                0
              );

        // Dest actual = for each dest tank: use the live actual value if the tank was checked
        // and an actual was typed, else fall back to planned (unchecked = no drop = full planned).
        const _actualDest = (operation?.dest_tanks || []).reduce((acc, dt) => {
          const key = String(dt?.tank_id);
          const isChecked = !!(completed?.[key] || completed?.[dt?.tank_id]);
          const actualVal = drops?.[key] ?? drops?.[dt?.tank_id];
          const plannedL = Number(dt?.arrow_quantity ?? dt?.quantity) || 0;
          if (isChecked && actualVal !== undefined && actualVal !== "") {
            return acc + inputToLiters(Number(actualVal) || 0, _inputUnit);
          }
          return acc + plannedL;
        }, 0);

        // Pomace actual = for each pomace: use the rectified value from the form if typed,
        // else fall back to the planned value (untouched defaultValue is not in form state).
        // Empty for DECANT/CUT (no pomace concept there), so the ceiling reduces to _sourceQty.
        const _actualPomaces = (operation?.pomaces || []).reduce((acc, p) => {
          const raw = data?.cellarman_pomaces?.[p?.waste_type];
          const plannedL = Number(p?.quantity) || 0;
          return acc + (raw !== undefined && raw !== "" && raw !== null
            ? inputToLiters(Number(raw) || 0, _inputUnit)
            : plannedL);
        }, 0);

        const _assigned = _actualDest + _actualPomaces;
        if (_sourceQty > 0 && _assigned > _sourceQty + 0.001) {
          toast(getLabel("operationOverAllocatedError"), {
            toastId: customIdConfirmOp,
            type: toast.TYPE.ERROR,
            position: toast.POSITION.BOTTOM_RIGHT,
          });
          return;
        }

        // Under-allocation ("every liter must be accounted for, either as wine in a
        // dest tank or as pomace waste") only applies to DESTEMMING/RACKING, whose
        // pomace is the designated accounting mechanism for loss. DECANT/CUT have no
        // pomace concept: a shortfall there is simply the calo/drop recorded on the
        // destination tank, and is expected/allowed.
        if (["DESTEMMING", "RACKING"].includes(operation?.type) && _sourceQty > 0 && _assigned < _sourceQty - 0.999) {
          const _missing = Math.round(_sourceQty - _assigned);
          toast(getLabel("operationUnderAllocatedError", { missing: _missing }), {
            toastId: customIdConfirmOp,
            type: toast.TYPE.ERROR,
            position: toast.POSITION.BOTTOM_RIGHT,
          });
          return;
        }
      }
    }
    console.log('requestPayload', requestPayload);
    // return
    const isStabilization = ["STABILIZATION_STATIC", "STABILIZATION_DYNAMIC", "STABILIZATION_RESIN"].includes(operation?.type);
    const isFiltering = operation?.type === "FILTERING";
    const isHomogenization = operation?.type === "HOMOGENIZATION_MASS";
    const isOxygenation = operation?.type === "OXYGENATION";
    const isCryoMaceration = operation?.type === "DESTEMMING" && operation?.subtype === "CRYO_MACERATION";
    const isOperationToBeStarted = (isStabilization || isFiltering || isHomogenization || isOxygenation || isCryoMaceration) && operation?.start_date === null;

    // Optimistic-concurrency: tell the server which tank state we acted on, so it
    // rejects (409) if the cellar content changed since we loaded the screen.
    const clientVersions = buildOperationVersions(operation, tanks);
    if (clientVersions) requestPayload.client_versions = clientVersions;

    const response = await toastAxios(
      () => isOperationToBeStarted ?
      startOperation(id, axiosPrivate)
      : completeOperation(id, requestPayload, axiosPrivate),
      customIdConfirmOp,
      { type: operation?.type || "" },
      getLabel,
    );
    console.log('response', response);

    // Stale view: the server rejected because a source tank changed. Refetch just
    // the conflicting tanks into Redux (the toast already told the user) and stay
    // on the screen so they can review the fresh values and re-confirm.
    if (response?.conflict) {
      const affected = conflictsToAffectedEntities(response.details?.conflicts || []);
      if (Object.keys(affected).length > 0) {
        const cacheManager = new CacheManager(dispatch, axiosPrivate);
        await cacheManager.processAffectedEntities(affected);
      }
      return;
    }

    if (response && response?.success) {

      const updated_op = response?.updated_data || null;
      console.log('DEBUG startOperation: updated_op=', updated_op, 'isOperationToBeStarted=', isOperationToBeStarted);
      if (updated_op && isOperationToBeStarted) {
        console.log('DEBUG: dispatching updateOp with:', updated_op);
        dispatch(updateOp(updated_op));
      } else {
        console.log('DEBUG: dispatching removeOp with id:', Number(id));
        dispatch(removeOp(({ id: Number(id) })));
        dispatch(removeExpOp({ id: Number(id) }));
      }

      // NEW: Surgical cache update using affected_entities from response
      if (response?.affected_entities) {
        const cacheManager = new CacheManager(dispatch, axiosPrivate);
        await cacheManager.processAffectedEntities(response.affected_entities);
      }
      // ⚠️ FALLBACK: If backend doesn't return affected_entities yet
      else {
        if (operation?.type === "ANALYSIS") {
          // dispatch(emptyExams());
        } else if (operation?.type === "CLEANING") {
          dispatch(emptyTanks());
        } else if (
          ["DESTEMMING", "CUT", "RACKING", "DECANT", "NEW_LOT", "BOTTLING"].includes(
            operation?.type
          )
        ) {
          dispatch(emptyLots());
          dispatch(emptyTanks());
        }
      }

      navigate(`/programming/`);
    }
  };

  const customIdUpdateProtocol = "custom-id-sample-data";
  const sendSampleData = async () => {
    const data = getValues();
    console.log('data', data)
    
    const requestPayload = {};
    if (!buildAnalysisPayload(data, requestPayload)) return;
    console.log('requestPayload sample', requestPayload);
    // return


    const response = await toastAxios(
        () => addAnalysisSample(id, requestPayload, axiosPrivate),
        customIdUpdateProtocol,
        { type: operation?.type || "" },
        getLabel,
      );
    
    console.log('response', response);
    if (response && response?.success) {
      
      const updated_op = response?.updated_data || null;
      if (updated_op) {
        dispatch(updateOp(updated_op));
        // setOperation(updated_op);
      } else {
        dispatch(removeOp(({ id: Number(id) })));
        dispatch(removeExpOp({ id: Number(id) }));
        await loadCompletedOperations();
      }

      setIsOpen(false);
      setStep(0);
      setAnalysisState("NOT_INSERTED");
      // const anResults = (await getAnalysisResults([operation?.main_id || operation?.id], axiosPrivate) || []);
      // const anRes = (await getAnalysisResults([operation?.main_id || operation?.id], axiosPrivate) || []);
      // const anResults = anRes?.sort((a, b) => a?.id - b?.id);
      // console.log(anResults, anResults?.length, operation?.src_tanks?.length, '----');
      // if (anResults?.length === operation?.src_tanks?.length)
      //   setAnalysisState("NOT_INSERTED");
      // navigate(`/programming/`);

      filterDisabledSubstances();

      setKey((prev) => prev + 1);
    }
  };

  const renderInsertAnalysisSample = () => {
    // const isCompleted = true;
    const disabled = isAnalysisExecutableOrSample(operation, analysisResults, lots, tanks, isNextToExecute);
    
    if (disabled)
      return (
        <Tooltip
          variant={"info"}
          html={isInvalidSrcTank ? getLabel("errorSrcTankNowEmpty")
            : getLabel("errorPreviousRecurrOpsNotCompleted")}
          place="right"
          events={["hover"]}
        >
          <CTA
            customClassName={`is_submit ${analysisState === "INSERTING" ? "complete" : "add"}`}
            disabled={disabled}
          >
            {getLabel("addRepetitiveAnalysisSampleOperation")}
          </CTA>
        </Tooltip>
      );
    else
      return (
        <CTA
          customClassName={`is_submit ${analysisState === "INSERTING" ? "complete" : "add"}`}
          onClick={() => operation?.type === "ANALYSIS" ? handleOpen() : sendSampleData() }
          disabled={disabled}
        >
          {analysisState === "NOT_SAMPLED"
            ? getLabel("addRepetitiveAnalysisSampleOperation")
            : getLabel("addRepetitiveAnalysisOperation")}
        </CTA>
      );
  };

  const handleImport = () => {
    setIsImport(true);
    handleOpenModal();
  };

  const cancel = () => {
    const column = getValues("column")?.map((c) => {
      return { value: "" };
    });
    setValue("column", column);
    if (analysisState === "INSERTING") setAnalysisState("NOT_INSERTED");
  };

  const handleExport = () => {
    const data = [];
    const analysisExams = operation?.treatments || [];
    
    operation?.src_tanks?.forEach((tank) => {
      const filteredExams = analysisExams?.filter((e) => e?.batch_id === tank?.batch_id) || [];
      filteredExams?.forEach((an) => {
        // if (operation?.treatments?.find((s) => s?.substance_id == an?.substance_id && s?.removed)) return true;
        const tmp = {};
        tmp[getLabel('tank')] = tank?.name;
        tmp[getLabel('tank_id')] = tank?.tank_id;
        tmp[getLabel('batch')] = tank?.old_batch_name;
        tmp[getLabel('batch_id')] = tank?.batch_id;
        tmp[getLabel('analysisSubstanceId')] = an?.substance_id;
        tmp[getLabel('analysisSubstanceName')] = getLabel(`substance_${an?.substance_name}`);
        tmp[getLabel('substanceRang')] = `${an?.min_range !== null ? String(an?.min_range) || "n/a" : "n/a"} - ${
          an?.max_range !== null ? String(an?.max_range) || "n/a" : "n/a"}`;
        tmp[getLabel('unit')] = an?.unit;
        tmp[getLabel('analysisLabel_id')] =  `AN-${operation?.custom_name || operation?.id}-${tank?.name || tank?.old_batch_name}`;
        tmp[getLabel('examResult')] = "";
        
        data.push(tmp);
      });
    });
    
    const worksheet = XLSX.utils.json_to_sheet(data);
    const wscols = [
      {wch: 13},  // tank
      {wch: 10},  // tank_id
      {wch: 13},  // batch
      {wch: 10},  // batch_id
      {wch: 10},  // analysis id
      {wch: 10},  // analysis name
      {wch: 13},  // analysis range
      {wch: 13},  // unit
      {wch: 20},  // analysis label
      {wch: 15},  // results
    ];
    worksheet['!cols'] = wscols;
    
    const workbook = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(workbook, worksheet, "Sheet1");
    XLSX.writeFile(
      workbook,
      `${getLabel('pdfType_ANALYSIS')}-${operation?.custom_name || id}_${format(new Date(), configs.shortDateFormat)}.xlsx`
    );
  };

  const renderUpdateButton = () => {
    if (!operation.repetitive ||
      (operation?.repetitive &&
        !operation.main_id &&
        Number(operation?.relative_id) === 1 &&
        Number(operation?.completed) === 0)) return (
      <NavLink to={`/programming/${getOperationUrlSegment(effectiveType)}/${id}/update`}>
        <CTA>{getLabel("updateOperation")}</CTA>
      </NavLink>)

    const isRepetitiveAndNotStarted = operation?.repetitive &&
      operation.main_id &&
      Number(operation?.completed) === 0;
    const isRepetitiveAndAlreadyStartedDisable = operation?.repetitive &&
      operation.main_id &&
      Number(operation?.completed) > 0;
    
    const disabled = isRepetitiveAndAlreadyStartedDisable;

    if (disabled)
      return (
      <Tooltip
        variant={"info"}
        html={
          isRepetitiveAndAlreadyStartedDisable ? getLabel("errorRecurrentOpAlreadyStarted")
          : ""
        }
        place="right"
        events={["hover"]}
      >
      <NavLink to={`/programming/${getOperationUrlSegment(effectiveType)}/${isRepetitiveAndNotStarted ? operation?.main_id : id}/update`}>
        <CTA disabled={disabled}>{getLabel("updateOperation")}</CTA>
      </NavLink>
      </Tooltip>
    )
    return (
      <NavLink to={`/programming/${getOperationUrlSegment(effectiveType)}/${isRepetitiveAndNotStarted ? operation?.main_id : id}/update`}>
        <CTA>{getLabel("updateOperation")}</CTA>
      </NavLink>)
  }

  const renderRollbackModal = () => {
    const dependents = rollbackImpact?.dependents || [];
    // can_rollback dall'anteprima ha priorità (è una verifica in tempo reale);
    // se l'anteprima non è ancora arrivata cadiamo sul flag dell'operazione.
    const canRollback =
      rollbackImpact?.can_rollback ?? operation?.can_rollback ?? false;
    const blockedReason = rollbackImpact?.blocked_reason;

    return (
      <Modal
        forwardRef={rollbackModalRef}
        isOpen={isRollbackOpen}
        closeHandle={closeRollbackModal}
        customClassName="confirmation rollback_modal recap"
      >
        <div className="confirmation-modal-container">
          <h2 className="title">{getLabel("rollbackOperation")}</h2>
          <br />

          {rollbackImpactLoading ? (
            <p className="description">{getLabel("toast_inProgress")}</p>
          ) : (
            <>
              <p className="description">
                {getLabel("modalRollbackOperationDescription", {
                  type: getLabel("operationType" + (operation?.type || "")),
                })}
              </p>

              {dependents.length > 0 && (
                <div className="rollback_dependents">
                  <p className="rollback_dependents_title">
                    {getLabel("rollbackDependentsWarning")}
                  </p>
                  <ul>
                    {dependents.map((dep) => (
                      <li key={dep?.id}>
                        {getLabel("operationType" + (dep?.type || "")) || dep?.type}
                        {dep?.completion_date
                          ? ` — ${format(new Date(dep.completion_date), "dd/MM/yyyy HH:mm")}`
                          : ""}
                        {dep?.has_snapshot === false ? ` (${getLabel("rollbackNoSnapshot")})` : ""}
                      </li>
                    ))}
                  </ul>
                </div>
              )}

              {!canRollback && (
                <p className="rollback_blocked">
                  {getLabel(blockedReason || "errorRollbackNotPossible")}
                </p>
              )}
            </>
          )}

          <div className="button-container">
            {canRollback && !rollbackImpactLoading && (
              <CTA
                customClassName={"dialog confirm"}
                isRed={false}
                disabled={isRollingBack}
                onClick={performRollback}
              >
                <p>{getLabel("modalConfirmButton")}</p>
              </CTA>
            )}
            <CTA
              customClassName={"dialog deny"}
              isRed={false}
              disabled={isRollingBack}
              onClick={closeRollbackModal}
            >
              {getLabel("modalDenyButton")}
            </CTA>
          </div>
        </div>
      </Modal>
    );
  };

  const renderRollbackButton = () => {
    if (!operation?.can_rollback) {
      return (
        <Tooltip
          variant={"info"}
          html={getLabel("errorRollbackNotPossible")}
          place="right"
          events={["hover"]}
        >
          <CTA disabled={true}>
            {getLabel("rollbackOperation")}
          </CTA>
        </Tooltip>
      );
    }
    return (
      <>
        <CTA onClick={openRollbackModal}>
          {getLabel("rollbackOperation")}
        </CTA>
        {renderRollbackModal()}
      </>
    );
  };

  const renderButtons = (isSubmit = false) => {
    const analysisResultsPerOperationId = analysisResults?.filter(
      (an) => an?.operation_id === operation?.id
    );
    // console.log('analysisResultsPerOperationId', analysisResultsPerOperationId, analysisState, analysisResults)
    if (isSubmit &&
      ["NOT_SAMPLED"]?.includes(analysisState) &&
      operation?.type === "ANALYSIS" &&
      // operation?.repetitive &&
      (analysisResultsPerOperationId?.length < operation?.src_tanks?.length ||
        analysisResultsPerOperationId?.length === 0)
    ) {
      return renderInsertAnalysisSample();
    }
    const completed = getValues("completed") || [];
    const actualValues = getValues("actual") || {};
    const dropForCheck = Object.values(actualValues).filter(v => v !== undefined && v !== '');
    const column = getValues("column") || {};
    const { disabled, tooltipMessage } = isOperationExecutable(operation, isInvalidSrcTank, analysisState, analysis, completed, dropForCheck, column, errors, isNextToExecute, hasAnalysisSampled);
    
    const isStabilization = ["STABILIZATION_STATIC", "STABILIZATION_DYNAMIC", "STABILIZATION_RESIN"].includes(operation?.type);
    const isFiltering = operation?.type === "FILTERING";
    const isHomogenization = operation?.type === "HOMOGENIZATION_MASS";
    const isOxygenation = operation?.type === "OXYGENATION";
    const isCryoMaceration = operation?.type === "DESTEMMING" && operation?.subtype === "CRYO_MACERATION";
    const isOperationStarted = (isStabilization || isFiltering || isHomogenization || isOxygenation || isCryoMaceration) && operation?.start_date !== null;

    if (isSubmit && (role === 3 || (role === ROLES.CLIENT &&
      users?.filter((u) => u?.type === "USER")?.length ===
        0)) && operation?.completion_date == null) {
      if (disabled) return (<div className="operation_info_container_row">
        <Tooltip
          variant={"info"}
          html={tooltipMessage ? getLabel(tooltipMessage) : ""}
          place="top"
          events={["hover"]}
          customClassName={"is_submit"}
        >
          <CTA customClassName={"complete is_submit"} disabled={disabled}>
            {isStabilization
              ? getLabel(isOperationStarted ? "endStabilizationOperation" : "startStabilizationOperation")
              : isFiltering
              ? getLabel(isOperationStarted ? "endFilteringOperation" : "startFilteringOperation")
              : isHomogenization
              ? getLabel(isOperationStarted ? "endHomogenizationOperation" : "startHomogenizationOperation")
              : isOxygenation
              ? getLabel(isOperationStarted ? "endOxygenationOperation" : "startOxygenationOperation")
              : isCryoMaceration
              ? getLabel(isOperationStarted ? "endCryoOperation" : "startCryoOperation")
              : operation?.type === "ANALYSIS"
                ? getLabel("completeRepetitiveAnalysisOperation")
                : getLabel("completeOperation")}
          </CTA>
        </Tooltip>
      </div>)
    else return (<div className="operation_info_container_row"><CTA
        customClassName={`${analysisState === "INSERTING" ? "complete" : "add"} is_submit`}
        onClick={() =>
          operation?.type === "ANALYSIS" ? handleOpen() : sendData()
        }
        disabled={disabled}
      >
        {isStabilization
          ? getLabel(isOperationStarted ? "endStabilizationOperation" : "startStabilizationOperation")
          : isFiltering
          ? getLabel(isOperationStarted ? "endFilteringOperation" : "startFilteringOperation")
          : isHomogenization
          ? getLabel(isOperationStarted ? "endHomogenizationOperation" : "startHomogenizationOperation")
          : isOxygenation
          ? getLabel(isOperationStarted ? "endOxygenationOperation" : "startOxygenationOperation")
          : isCryoMaceration
          ? getLabel(isOperationStarted ? "endCryoOperation" : "startCryoOperation")
          : operation?.type === "ANALYSIS"
            ? analysisState === "INSERTING"
              ? getLabel("completeRepetitiveAnalysisOperation")
              : analysisState === "NOT_SAMPLED"
              ? getLabel("addRepetitiveAnalysisSampleOperation")
              : analysisState === "NOT_INSERTED"
              ? getLabel("addRepetitiveAnalysisOperation")
              : getLabel("completeOperation")
                : getLabel("completeOperation")}
      </CTA></div>)
    }

    if (disabled)
      return (
        <>
          {operation?.type === "ANALYSIS" && analysisState === "INSERTING" && (
            <CTA onClick={cancel} disabled={false}>
              {getLabel("deny")}
            </CTA>
          )}
          {operation?.type === "ANALYSIS" && (
            <Tooltip
              variant={"info"}
              html={tooltipMessage ? getLabel(tooltipMessage) : ""}
              // html={
              //   isInvalidSrcTank ? getLabel("errorSrcTankNowEmpty")
              //   : isCompleted
              //     ? getLabel("errorOpsCompleted")
              //     : repetitiveDisabled
              //     ? getLabel("errorPreviousRecurrOpsNotCompleted")
              //     : checkboxDisabled
              //     ? getLabel("errorNotAllCheckboxCompleted")
              //     : repetitiveIterationDisabled
              //     ? getLabel("errorNotAllAnalysisCompleted")
              //     : ""
              // }
              place="right"
              events={["hover"]}
            >
              {operation?.on_tank && (
                <CTA customClassName={"complete"} disabled={disabled}>
                  {getLabel("addRepetitiveAnalysisOperationExcel")}
                </CTA>
              )}
            </Tooltip>
          )}  
        </>
      );
    else
      return (
        <>
          {operation?.type === "ANALYSIS" && analysisState === "INSERTING" && (
            <CTA onClick={cancel} disabled={false}>
              {getLabel("deny")}
            </CTA>
          )}
          {operation?.type === "ANALYSIS" && operation?.on_tank && (
            <CTA
              onClick={handleImport}
              customClassName={
                analysisState === "INSERTING" ? "complete" : "add"
              }
              disabled={analysisState !== "NOT_INSERTED"}
            >
              {getLabel("addRepetitiveAnalysisOperationExcel")}
            </CTA>
          )}
          {operation?.type === "ANALYSIS" && operation?.on_tank && (
            <CTA onClick={requestLabelPDF}>
              {getLabel("printAnalysisOperationLabel")}
            </CTA>
          )}
        </>
      );
  };

  const handleOpen = () => {
    if (analysisState === "NOT_SAMPLED") {
      handleOpenModal();
      // setAnalysisState('NOT_INSERTED');
    } else if (analysisState === "NOT_INSERTED") {
      setAnalysisState("INSERTING");
    } else if (analysisState === "INSERTING") {
      handleManualAddOpenModal();
    }
    //  else if (analysisState === "INSERTED") {
    //   sendData();
    // }
  };

  const handleConfirm = useCallback(() => {
    if (isManual) return handleManualAddConfirmModal();
    if (!isImport) return sendSampleData();
    return handleImportConfirmModal();
    // `operation` deve essere tra le dipendenze: senza, la callback cattura la closure di
    // sendSampleData di un render iniziale in cui operation era ancora undefined (in
    // caricamento) → on_tank falsy → payload con batch_id al posto di tank_id.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isManual, isImport, operation]);


  const renderNavMenu = () => {
    const navClickHandler = () => {
      navigate(-1);
    };

    return (
      <div className="primary_container_menu">
        <div className="primary_container_row">
          <Button arrowDirection="left" onClick={navClickHandler} />
          <div className="title_container">
            <h2>
              {
              // pageName +
                computeOperationRow(operation, treatments, analysis, getLabel)

                // // type === 'ADD' ? computeAddOpTypeString() : 
                // (computeAdditionalTypeString() +
                // computeRecurrentString())
              }
            </h2>
          </div>
        </div>
        <div className="primary_container_row ctas">
          {operation?.completion_date === null &&
            (role === ROLES.USER ||
              (role === ROLES.CLIENT &&
                users?.filter((u) => u?.type === "USER")?.length === 0)) &&
            // ["ANALYSIS", "TREATMENT"].includes(operation?.type) &&
            // operation?.completion_date === null &&
            renderButtons()}
          {role === ROLES.CLIENT && operation?.completion_date === null && (
            <>
            {renderUpdateButton()}
              <NavLink to={`/programming/${getOperationUrlSegment(effectiveType)}/${id}/duplicate`}>
                <CTA>{getLabel("copyOperation")}</CTA>
              </NavLink>

              <CTA onClick={handleOpenModal}>{getLabel("deleteOperation")}</CTA>
              <ConfirmationModal
                isOpen={isOpen}
                onConfirm={handleFormSubmit}
                onClose={handleCloseModal}
                description={getLabel(
                  operation?.repetitive
                    ? "modalDeleteRepetitiveOperationDescription"
                    : "modalDeleteOperationDescription",
                  {
                    type: getLabel("operationType" + operation?.type),
                  }
                )}
              ></ConfirmationModal>
            </>
          )}
          {role === ROLES.CLIENT &&
            isHistoryView &&
            operation?.completion_date != null && (
              renderRollbackButton()
            )
          }
          {role === ROLES.USER &&
            operation?.completion_date === null &&
            ["ANALYSIS", "TREATMENT"].includes(operation?.type) && (
              <>
                <ConfirmationModal
                  isOpen={isOpen}
                  onConfirm={handleConfirm}
                  onClose={handleCloseModal}
                  description={
                    isManual
                      ? getLabel("modalNewAnalysisAnalystAdditional")
                      : !isImport
                      ? getLabel("modalNewAnalysisExamAdditional")
                      : getLabel("modalNewAnalysisExamExcelAdditional")
                  }
                  isOperationRecap={true}
                  errors={errors}
                  getValues={getValues}
                  isAnalysis={true}
                  setError={setError}
                  clearErrors={clearErrors}
                  totalStep={
                    isManual
                      ? 0
                      : !isImport
                      ? operation?.src_tanks?.length - 1
                      : 0
                  }
                  step={step}
                  setStep={setStep}
                  type={
                    isManual ? "ANALYST" : !isImport ? "" : "EXCEL_ANALYSIS"
                  }
                >
                  <OperationRecap
                    type={
                      isManual
                        ? "ANALYST"
                        : !isImport
                        ? "ANALYSIS"
                        : "EXCEL_ANALYSIS"
                    }
                    control={control}
                    register={register}
                    getValues={getValues}
                    setValue={setValue}
                    setError={setError}
                    errors={errors}
                    configs={configs}
                    lots={lots}
                    tanks={tanks}
                    totalStep={operation?.src_tanks?.length - 1}
                    step={step}
                    setStep={setStep}
                    setFocus={setFocus}
                    src_tanks={operation?.src_tanks}
                    setFile={setFile}
                    columnStartIndex={
                      !isImport
                        ? 0
                        : Object.keys(analysis?.[0]?.analysis || {})?.length *
                          (operation?.src_tanks?.length || 1)
                    }
                  />
                </ConfirmationModal>
              </>
            )}
          <CTA onClick={requestPDF}>{getLabel("print")}</CTA>
          {operation?.completion_date === null &&
            operation?.type === "ANALYSIS" && (
              <CTA onClick={handleExport}>
                {getLabel("operationExcelExport")}
              </CTA>
            )}
          {operation?.completion_date === null &&
            operation?.type === "ANALYSIS" && (
              <CTA onClick={handleImport}>
                {getLabel("operationExcelImport")}
              </CTA>
            )}
        </div>
      </div>
    );
  };

  const customIdAddNote = "custom-id-add-note";
  const storeNote = async (
    axiosPrivate,
    dispatch,
    setValue,
    getValues,
    getLabel,
    operation,
    user_id,
    userSockets,
    users,
    addChatNote,
  ) => {
    const newNote = getValues("new_note") || "";
    if (!newNote || newNote?.length === 0) return;
    
    const requestPayload = { message: newNote };
    const response = await toastAxios(
      () => addOperationNote(
        operation?.id,
        requestPayload,
        axiosPrivate
      ),
      customIdAddNote,
      { name: "" },
      getLabel,
    );
    if (response && response?.success) {
      const user_ids = new Set();
      user_ids.add(user_id);
      user_ids.add(...operation?.cellarman_ids);

      if (response.success) {
        // dispatch(empty());
        setValue("new_note", "");

        const tmpNote = 
        {
          "message": newNote,
          "from_user_id": user_id,
          "user_id": user_id,
          "timestamp": new Date(),
        };

        addChatNote(tmpNote);
      }
    }
  };

  return (
    <div className="primary_container">
      <div className="primary_container_top">
      <SecondaryBar breadCrumb={[{ label: getLabel(operation?.completion_date? "programmingHistoryNavLink" : "programmingNavLink", pageName), link: `/programming${operation?.completion_date ? "/history" : ""}` }]} />
      {renderNavMenu()}
      </div>
        <div className="primary_container_bottom">
        {operation?.protocol_application_id && virtualTanks && !isNextToExecute && (
          <div className="virtual_toggle_row">
            <Switch
              leftOption={{ label: getLabel("virtualModeReal") }}
              rightOption={{ label: getLabel("virtualModeVirtual") }}
              status={isVirtualMode}
              toggleHandler={() => setIsVirtualMode(v => !v)}
            />
          </div>
        )}
        {renderDetail()}
        {renderDrugBatchModal()}
        {((role === 3 || (role === ROLES.CLIENT &&
        users?.filter((u) => u?.type === "USER")?.length ===
          0)) && operation?.completion_date == null) && renderButtons(true)}
        <div className="operation_info_container_row">
          <ChatArea
            name="chat"
            register={register}
            label={getLabel("lotNotes")}
            placeholder={getLabel("lotNotesPlaceholder")}
            users={users}
            user_id={auth?.user_id}
            // tooltip={getLabel("lotNotesTooltip")}
            configs={configs}
            operation={operation}
            storeNote={() =>
              storeNote(
                axiosPrivate,
                dispatch,
                setValue,
                getValues,
                getLabel,
                operation,
                auth?.user_id,
                userSockets,
                users,
                addChatNote,
              )
            }
          />
        </div>
        <div className="operation_info_container_row additional">
          <Label label={getLabel("demandant")}>
            {users?.find?.(
              (user) => Number(user.id) === Number(operation?.user_id)
            )?.username || ""}
          </Label>
          <Label label={getLabel("updateDate")}>
            {format(new Date(operation?.update_date || null), configs.dateFormat)}
          </Label>
        </div>
        {operation?.id && (
          <div style={{ padding: "0 0 32px" }}>
            <AuditLogWidget entityType="OPERATION" entityId={operation.id} />
          </div>
        )}
      </div>
    </div>
  );
};

export default OperationsDetail;
